import Alpine from 'alpinejs'
import Chart from 'chart.js/auto'

// Plugin to draw a bold black line at y=0 when the scale includes zero (used only by charts in this file)
const zeroLinePlugin = {
  id: 'flexibilityZeroLine',
  afterDraw (chart) {
    const y = chart.scales.y
    if (!y) return
    const min = y.min
    const max = y.max
    if (min > 0 || max < 0) return
    const y0 = y.getPixelForValue(0)
    const left = chart.chartArea.left
    const right = chart.chartArea.right
    const ctx = chart.ctx
    ctx.save()
    ctx.beginPath()
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 1
    ctx.moveTo(left, y0)
    ctx.lineTo(right, y0)
    ctx.stroke()
    ctx.restore()
  }
}

document.addEventListener('alpine:init', () => {
  Alpine.data('flexibilityQuantificationScenario', () => {
    const loc = window.location.href
    const temps = [-5, 0, 5, 10, 15]
    const flexibilityCharts = []
    const flexibilityData = []

    return {
      years: [2021, 2025, 2030, 2035, 2040, 2045, 2050],
      discreteQuantification: null,
      isLoading: false,
      chartReady: false,

      init () {
        this.chartReady = false

        // Wait for next tick to ensure DOM is ready
        this.$nextTick(() => {
          this.initializeCharts()
        })
      },

      initializeCharts () {
        if (this.chartReady) return
        temps.forEach((t) => {
          const chartElement = document.getElementById(`flexibilityChart${t}`)
          const flexibilityChart = new Chart(chartElement, {
            type: 'line',
            plugins: [zeroLinePlugin],
            data: {
              datasets: [{}, {}]
            },
            options: {
              maintainAspectRatio: false,
              plugins: {
                tooltip: {
                  callbacks: {
                    title: (tooltipItems) => {
                      const x = tooltipItems[0]?.parsed?.x
                      return x != null ? `${x.toFixed(1)} minutes` : ''
                    },
                  }
                }
              },
              interaction: {
                mode: 'nearest',
                axis: 'x',
                intersect: false
              },
              elements: {
                line: {
                  tension: 0.4
                }
              },
              scales: {
                x: {
                  type: 'linear',
                  min: 0,
                  max: 1440,
                  title: {
                    display: true,
                    text: 'Duration (hours)'
                  },
                  ticks: {
                    callback: function(value) {
                      // Only show labels at integer hours (multiples of 60 minutes)
                      if (value % 60 === 0) {
                        return (value / 60).toString()
                      }
                      return null
                    }
                  }
                },
                y: {
                  title: {
                    display: true,
                    text: 'Flexibility magnitude (MW)'
                  }
                }
              }
            }
          })

          flexibilityData.push(document.getElementById(`flexibilityChart${t}-data`))
          flexibilityCharts.push(flexibilityChart)
        })
        this.chartReady = true

        // Setup the reactive effects
        this.setupEffects()
      },

      setupEffects () {
        if (!this.chartReady) return

        const store = Alpine.store('scenarioState')

        // Effect for fetching data when store changes
        Alpine.effect(() => {
          let region = 'GB'
          if (store.selectedSpatialResolution === 'Country') {
            region = store.selectedCountry
          }
          if (store.selectedSpatialResolution === 'LA') {
            region = store.selectedLA
          }
          let year = store.selectedYear
          // Flexibility quantification runs from 2020, not 2021
          if (year === '2021') year = 2020

          this.isLoading = true

          fetch(`${loc}/${region}/${year}/data.json`)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
              }
              return response.json()
            })
            .then(data => {
              this.discreteQuantification = data.discreteQuantification
              this.isLoading = false
            })
            .catch(() => {
              this.discreteQuantification = null
              this.isLoading = false
            })
        })

        // Effect for updating chart when data changes
        Alpine.effect(() => {
          if (!this.chartReady || !this.discreteQuantification) {
            return
          }

          const chartColours = [
            '#36A2EB',
            '#FF6384'
          ]
          const types = ['Demand turn-down', 'Demand turn-up']
          const displayTypes = ['Turn-down', 'Turn-up']

          temps.forEach((temp, index) => {
            types.forEach((flexibilityType, flexibilityTypeIndex) => {
              const filteredData = this.discreteQuantification
                .filter(entry =>
                  parseFloat(entry['Outdoor_air_temperature_(°C)']) === temp &&
                  entry.Flexibility_service_type === flexibilityType
                )
                .sort((a, b) => parseInt(a['Duration_(s)']) - parseInt(b['Duration_(s)']))
                .map(entry => ({
                  x: parseInt(entry['Duration_(s)'], 10) / 60,
                  y: entry['Total_flexibility_provided_(kW)'] / 1000
                }))

              flexibilityCharts[index].data.datasets[flexibilityTypeIndex] = {
                borderColor: chartColours[flexibilityTypeIndex],
                backgroundColor: chartColours[flexibilityTypeIndex],
                borderWidth: 0.5,
                pointRadius: 1,
                label: displayTypes[flexibilityTypeIndex],
                data: filteredData
              }
            })
            flexibilityCharts[index].update()
            flexibilityData[index].value = JSON.stringify(flexibilityCharts[index].data)
          })
        })
      }
    }
  })
})
